/**
 * The advisor chat. Sends the conversation and the calculator's own results
 * (numbers only — never documents) to /api/chat on this site's Worker.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { fmtMoney } from '../intake/parseAnswer.js';

export interface CalcSummary {
  industry: string; naics2: string; state: string; dealType: string; useOfFunds: string;
  requestedLoan: number | null; equity: number | null; dscrTarget: number;
  ratePct: number | null; termYears: number | null;
  maxLoan: number | null; growthMaxLoan: number | null; limitingYear: string | null;
  verdict: string | null; minDscr: number | null;
  years: { label: string; cashFlow: number; dscr: number | null }[];
  flags: string[];
  sbaMinimum: { rule: string; minDscr: number; basis: string; maxLoan: number; requestedDscr: number | null; meets: boolean | null } | null;
}

interface Msg { role: 'user' | 'assistant'; content: string; lenders?: { name: string; city: string; hq: string; loans: number; medianAmount: number }[] }

const STARTERS = [
  'What would make my loan stronger?',
  'Which lenders should I talk to?',
  'What documents do I need?',
  'Can I add back my salary?',
];

export function ChatPanel({ open, onClose, summary }: { open: boolean; onClose: () => void; summary: CalcSummary }) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => { logRef.current?.scrollTo({ top: logRef.current.scrollHeight }); }, [msgs, busy]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const send = async (content: string) => {
    const q = content.trim();
    if (!q || busy) return;
    const next: Msg[] = [...msgs, { role: 'user', content: q }];
    setMsgs(next); setText(''); setBusy(true); setError(null);
    try {
      const res = await fetch('/api/chat', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ messages: next.map(({ role, content: c }) => ({ role, content: c })), summary }),
      });
      const data = await res.json().catch(() => ({})) as { reply?: string; error?: string; lenders?: Msg['lenders'] };
      if (!res.ok || !data.reply) throw new Error(data.error ?? 'The advisor is unavailable right now.');
      setMsgs([...next, { role: 'assistant', content: data.reply, ...(data.lenders ? { lenders: data.lenders } : {}) }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally { setBusy(false); }
  };

  const submit = (e: FormEvent) => { e.preventDefault(); void send(text); };

  if (!open) return null;
  return (
    <aside className="chat-panel" role="dialog" aria-label="SBA loan advisor" aria-modal="false">
      <div className="spread chat-panel-head">
        <strong>Loan advisor</strong>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Close advisor">×</button>
      </div>
      <div className="chat-log chat-panel-log" ref={logRef}>
        {msgs.length === 0 && (
          <div className="stack">
            <p className="small muted" style={{ margin: 0 }}>Ask about your estimate, add-backs, documents or lenders. The advisor sees your calculator results, not your files.</p>
            <div className="chips">{STARTERS.map((s) => <button key={s} type="button" className="chip" onClick={() => void send(s)}>{s}</button>)}</div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`bubble ${m.role}`}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
            {m.lenders && m.lenders.length > 0 && (
              <ul className="small" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {m.lenders.map((l) => <li key={l.name}><strong>{l.name}</strong> — {l.loans} SBA loans, typical {fmtMoney(l.medianAmount)} ({l.city}, {l.hq})</li>)}
              </ul>
            )}
          </div>
        ))}
        {busy && <div className="bubble assistant faint">Thinking…</div>}
        {error && <p className="error-text small" role="alert">{error}</p>}
      </div>
      <form className="row chat-panel-form" onSubmit={submit}>
        <input ref={inputRef} className="input" style={{ flex: 1 }} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Type a question…" aria-label="Your question" maxLength={2000} />
        <button className="btn primary small" type="submit" disabled={busy || !text.trim()}>Send</button>
      </form>
      <p className="small faint" style={{ margin: 0, padding: '0 14px 12px' }}>General guidance, not a loan offer. Messages go to Cloudflare to generate replies and are not stored by Tyche.</p>
    </aside>
  );
}
